"use client";

import { memo, useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { FiEdit3 } from "react-icons/fi";
import { useTranslations } from "next-intl";

type CopyPopUpProps = {
    isOpen: boolean;
    title?: string;
    exerciseName: string;
    exerciseNameEditable?: boolean;
    detailLine: string;
    copyId: string;
    isPreparing?: boolean;
    isCopying?: boolean;
    onClose: () => void;
    onConfirmCopy: (exerciseName: string) => void;
};

function CopyPopUp({
    isOpen,
    title,
    exerciseName,
    exerciseNameEditable = false,
    detailLine,
    copyId,
    isPreparing = false,
    isCopying = false,
    onClose,
    onConfirmCopy,
}: CopyPopUpProps) {
    const tCP = useTranslations("copyPopUp");
    const [draftExerciseName, setDraftExerciseName] = useState(exerciseName);

    useEffect(() => {
        if (isOpen) {
            setDraftExerciseName(exerciseName);
        }
    }, [isOpen, exerciseName]);

    if (!isOpen) {
        return null;
    }

    const resolvedTitle = title && title.trim() ? title : tCP("copyOrNot");

    return (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4" onClick={onClose}>
            <div
                className="w-full max-w-[450px] rounded-2xl bg-white px-6 pb-6 pt-5 shadow-[0_20px_45px_rgba(0,0,0,0.25)]"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="mb-2 flex items-start justify-between gap-3">
                    <h2 className="text-xl font-bold text-[#3d3d3d]">{resolvedTitle}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f0f2f4] text-[#9499a0] hover:bg-[#e5e9ed]"
                        aria-label="ปิด"
                    >
                        <IoClose size={20} />
                    </button>
                </div>

                <p className="mb-3 text-sm text-[#7a7a7a]">
                    {tCP("systemMessage")}
                </p>

                <div className="space-y-3">
                    <div>
                        <p className="mb-1 text-lg font-semibold text-[#4a4a4a]">{tCP("exercise")}</p>
                        <div className="relative">
                            <input
                                value={exerciseNameEditable ? draftExerciseName : exerciseName}
                                readOnly={!exerciseNameEditable}
                                onChange={(event) => setDraftExerciseName(event.target.value)}
                                className={`h-11 w-full rounded-lg border border-[#d8dde2] px-3 text-[17px] text-[#4a4a4a] outline-none ${
                                    exerciseNameEditable
                                        ? "bg-white pr-10 focus:border-[#079fe3]"
                                        : "bg-[#f9fbfc]"
                                }`}
                            />
                            {exerciseNameEditable ? (
                                <FiEdit3
                                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7a7a]"
                                    size={18}
                                    aria-hidden
                                />
                            ) : null}
                        </div>
                    </div>

                    <div>
                        <p className="mb-1 text-lg font-semibold text-[#4a4a4a]">{tCP("detail")}</p>
                        <input
                            value={detailLine}
                            readOnly
                            className="h-11 w-full rounded-lg border border-[#d8dde2] bg-[#f9fbfc] px-3 text-[17px] text-[#4a4a4a] outline-none"
                        />
                    </div>

                    <div>
                        <p className="mb-1 text-lg font-semibold text-[#4a4a4a]">{tCP("id")}</p>
                        <input
                            value={isPreparing ? tCP("preparingLink") : copyId}
                            readOnly
                            className="h-11 w-full rounded-lg border border-[#d8dde2] bg-[#f9fbfc] px-3 text-[17px] text-[#4a4a4a] outline-none"
                        />
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isCopying}
                        className="text-[16px] font-bold leading-none text-[#3ec8ff] hover:text-[#23aee6] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {tCP("cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const submittedName = exerciseNameEditable
                                ? draftExerciseName.trim() || exerciseName
                                : exerciseName;
                            onConfirmCopy(submittedName);
                        }}
                        disabled={isPreparing || !copyId || isCopying}
                        className="h-12 min-w-[120px] rounded-lg bg-[#079fe3] px-4 text-[16px] font-bold leading-none text-white hover:bg-[#008fd0] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isCopying ? tCP("copying") : tCP("confirmCopy")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default memo(CopyPopUp);
